import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Result } from 'react-zxing';

import { useAppDispatch } from '../../../../app/redux/hooks';
import QrScanner from '../../../../common/components/qr-scanner/QrScanner';
import {
  Customer,
  CustomerPhoto,
  QrCodeReaderType,
  useLazyGetCustomerPhotoQuery,
  useLazyGetCustomerQuery,
  useLazyGetTransactionQuery,
  useLinkCustomerLoyaltyCodeMutation,
} from '../../../api';
import { useOperatorSession, useWorkstation } from '../../../appState';
import { showErrorDialog } from '../../../error-dialog/errorDialogSlice';
import { setLoyaltyLinkInProgress, setTransaction, useAddToTransactionInProgress, useTransaction } from '../../orderSlice';

import ClaimCardModal from './ClaimCardModal';

import './loyalty.sass';

const KeyboardScanner = ({ onResult }: { onResult: (data: string) => void }) => {
  const keyboardState = useRef('');
  useEffect(() => {
    const eventHandler = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        onResult(keyboardState.current);
        keyboardState.current = '';
      } else if (e.key.length === 1) keyboardState.current = keyboardState.current + e.key;
    };
    window.addEventListener('keydown', eventHandler);
    return () => window.removeEventListener('keydown', eventHandler);
  }, [onResult]);

  return <div className="pos-servers-overview__icon pos-servers-overview__icon_role_scan"></div>;
};

interface LoyaltyWarning {
  warningType:
    | 'unknown'
    | 'not-verified'
    | 'not-active'
    | 'invalid-code'
    | 'expired-code'
    | 'not-loyalty'
    | 'customer-not-found'
    | 'code-redeemed'
    | 'customer-not-verified'
    | 'customer-inactive'
    | 'failed-loyalty'
    | 'photo-verification-required';
  transactionId: number;
}

function Loyalty() {
  const workstation = useWorkstation();
  const operatorSession = useOperatorSession();
  const transaction = useTransaction();
  const [getCustomerPhoto, { isLoading: isLoadingPhoto }] = useLazyGetCustomerPhotoQuery();
  const [getCustomer, { isFetching: isFetchingCustomer }] = useLazyGetCustomerQuery();
  const [getTransaction] = useLazyGetTransactionQuery();
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const [triggerLinkLoyalty, { isLoading }] = useLinkCustomerLoyaltyCodeMutation();
  const [loyaltyCode, setLoyaltyCode] = useState('');
  const [loyaltyWarning, setLoyaltyWarning] = useState<LoyaltyWarning | null>(null);
  const [customerCode, setCustomerCode] = useState<string | null>(null);
  const [customerPhoto, setCustomerPhoto] = useState<CustomerPhoto | null>(null);
  const [unclaimedCustomer, setUnclaimedCustomer] = useState<Customer | null>(null);
  const [scannedLoyaltyGuid, setScannedLoyaltyGuid] = useState<string | null>(null);
  const useIsAdding = useAddToTransactionInProgress();

  const [hasScanned, setHasScanned] = useState(false);

  const keyboardResult = (data: string) => {
    if (data.length >= 32) {
      setHasScanned(true);
      setLoyaltyCode(data);
    }
  };

  const scannerResult = (data: Result) => {
    const text = data.getText();
    if (text.length >= 32) {
      setHasScanned(true);
      setLoyaltyCode(text);
    }
  };

  const doTriggerLinkLoyalty = useCallback(
    async (customerCode: string, verifiedPhoto: boolean) => {
      if (operatorSession === undefined || transaction.length !== 1) {
        dispatch(
          showErrorDialog({
            dismissible: true,
            message: 'No operator session or split transaction found',
          }),
        );
        return;
      }
      const transactionId = transaction[0].id;
      console.info('Linking loyalty code', customerCode, 'to transaction', transactionId);
      // Blocks Pay / tender until the link and the refreshed transaction are both in place
      dispatch(setLoyaltyLinkInProgress(true));
      triggerLinkLoyalty({
        transactionId,
        loyaltyCode: customerCode,
        operatorId: operatorSession.operator.id,
        verifiedPhoto: verifiedPhoto,
      })
        .unwrap()
        .then(async () => {
          console.info('Linking loyalty succeeded for transaction', transactionId);
          try {
            const txNew = await getTransaction(transactionId).unwrap();
            dispatch(setTransaction([txNew]));
          } catch (err) {
            console.warn('Failed to refresh transaction after loyalty link', JSON.stringify(err));
          }
          navigate('/order');
        })
        .catch((err) => {
          console.warn('Linking loyalty failed for transaction', transactionId, JSON.stringify(err));
          if (err && err.status) {
            if (err.status === 412) {
              const typeErr: string = err.data.type;
              const contactIdStr: string = err.data.instance ?? '-1';
              const contactId: number = parseInt(contactIdStr, 10);
              const errorParts = typeErr.split('#');
              if (errorParts.length === 2) {
                const warning: LoyaltyWarning = {
                  transactionId: transaction[0].id,
                  warningType: 'unknown',
                };
                const code = errorParts[1];
                switch (code) {
                  case 'IS_VOUCHER_POOL':
                    warning.warningType = 'invalid-code';
                    break;
                  case 'CUSTOMER_NOT_FOUND':
                    warning.warningType = 'customer-not-found';
                    break;
                  case 'VOUCHER_EXPIRED':
                    warning.warningType = 'expired-code';
                    break;
                  case 'NOT_LOYALTY_SCANNED':
                    warning.warningType = 'not-loyalty';
                    break;
                  case 'VOUCHER_NOT_FOUND':
                    warning.warningType = 'invalid-code';
                    break;
                  case 'VOUCHER_REDEEMED':
                    warning.warningType = 'code-redeemed';
                    break;
                  case 'VOUCHER_NOT_VERIFIED':
                    warning.warningType = 'not-verified';
                    break;
                  case 'POOL_REQUIRES_VERIFICATION':
                    warning.warningType = 'invalid-code';
                    break;
                  case 'VOUCHER_REDEEM_FAILED':
                    warning.warningType = 'failed-loyalty';
                    break;
                  case 'CUSTOMER_INACTIVE':
                    warning.warningType = 'customer-inactive';
                    break;
                  case 'CUSTOMER_NOT_VERIFIED':
                    warning.warningType = 'customer-not-verified';
                    break;
                  case 'CUSTOMER_PHOTO_VERIFICATION_REQUIRED':
                    warning.warningType = 'photo-verification-required';
                    getCustomerPhoto(contactId)
                      .unwrap()
                      .then(setCustomerPhoto)
                      .catch(() => setCustomerPhoto(null));
                    setCustomerCode(customerCode);
                    break;
                }
                setLoyaltyWarning(warning);
              }
            } else {
              dispatch(
                showErrorDialog({
                  dismissible: true,
                  message: 'An error occurred linking the loyalty code',
                }),
              );
            }
          }
        })
        .finally(() => dispatch(setLoyaltyLinkInProgress(false)));
    },
    [dispatch, getCustomerPhoto, getTransaction, navigate, operatorSession, transaction, triggerLinkLoyalty],
  );

  useEffect(() => {
    if (loyaltyCode.length >= 32 && transaction && transaction.length === 1 && operatorSession) {
      // First, check if the customer exists and if they're an unclaimed physical card
      getCustomer(loyaltyCode)
        .unwrap()
        .then((customer) => {
          // Check if this is an unclaimed physical card
          if (customer.isPhysicalCard && !customer.firstName) {
            // Store the scanned GUID and show claim card modal
            setScannedLoyaltyGuid(loyaltyCode);
            setUnclaimedCustomer(customer);
          } else {
            // Normal flow - proceed with linking
            doTriggerLinkLoyalty(loyaltyCode, false);
          }
        })
        .catch((err) => {
          // If customer doesn't exist, still try to link (will handle error in doTriggerLinkLoyalty)
          console.warn('Failed to get customer, trying to link anyway:', err);
          doTriggerLinkLoyalty(loyaltyCode, false);
        });

      setLoyaltyCode('');
    }
  }, [doTriggerLinkLoyalty, getCustomer, loyaltyCode, operatorSession, transaction]);

  const handleClaimSuccess = (updatedCustomer: Customer) => {
    console.info('Card claimed successfully:', updatedCustomer);
    setUnclaimedCustomer(null);
    // Link the now-claimed customer to the transaction using the original scanned GUID
    if (scannedLoyaltyGuid) doTriggerLinkLoyalty(scannedLoyaltyGuid, false);
  };

  const handleSkipClaim = () => {
    console.info('Skipping card claim, proceeding with unclaimed card');
    if (scannedLoyaltyGuid) {
      setUnclaimedCustomer(null);
      // Proceed with unclaimed card using the original scanned GUID
      doTriggerLinkLoyalty(scannedLoyaltyGuid, false);
    }
  };

  const handleCancelClaim = () => {
    console.info('Cancelling card claim');
    setUnclaimedCustomer(null);
    setScannedLoyaltyGuid(null);
    setHasScanned(false);
  };

  // Show claim modal if we have an unclaimed customer
  if (unclaimedCustomer && scannedLoyaltyGuid) {
    return (
      <ClaimCardModal
        customer={unclaimedCustomer}
        loyaltyGuid={scannedLoyaltyGuid}
        onClaimed={handleClaimSuccess}
        onSkip={handleSkipClaim}
        onCancel={handleCancelClaim}
      />
    );
  }

  if (workstation === undefined) return <div>No WS</div>;
  return (
    <div className="pos-modal-portal">
      <div
        className="pos-modal-overlay"
        onClick={(e) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((e.target as any).className === 'pos-modal-overlay' && !isLoading && !isFetchingCustomer) navigate('/order');
        }}
      >
        <div className="pos-modal pos-modal_role_servers-overview">
          <div className="pos-modal__content">
            <div className="pos-servers-overview">
              <div className="pos-servers-overview__header">
                <h2 className="pos-servers-overview__title">Scan QR Code</h2>
              </div>
              {useIsAdding === true && (
                <div className="loyalty-photo-verification">
                  <p>Completing adding items to basket, please wait...</p>
                </div>
              )}
              {useIsAdding === false && (
                <>
                  {hasScanned === false && workstation.qrCodeReaderType === QrCodeReaderType.Scanner && (
                    <KeyboardScanner onResult={keyboardResult} />
                  )}
                  {hasScanned === false && workstation.qrCodeReaderType === QrCodeReaderType.Camera && (
                    <div className="pos-qr-scanner">
                      <QrScanner onResult={scannerResult} constraints={{ video: { facingMode: 'environment' } }} />
                    </div>
                  )}
                  {hasScanned === true && loyaltyWarning !== null && loyaltyWarning.warningType !== 'photo-verification-required' && (
                    <div className="loyalty-failed">
                      {loyaltyWarning && (
                        <p>
                          Loyalty failed due to <br />
                          <span>
                            {loyaltyWarning.warningType == 'not-verified' && 'NOT VERIFIED'}
                            {loyaltyWarning.warningType == 'not-active' && 'CUSTOMER DISABLED'}
                            {loyaltyWarning.warningType == 'expired-code' && 'CODE EXPIRED'}
                            {loyaltyWarning.warningType == 'invalid-code' && 'CODE INVALID'}
                            {loyaltyWarning.warningType == 'not-loyalty' && 'INVALID QR CODE'}
                            {loyaltyWarning.warningType == 'customer-not-found' && 'CUSTOMER NOT FOUND'}
                            {loyaltyWarning.warningType == 'code-redeemed' && 'VOUCHER ALREADY REDEEMED'}
                            {loyaltyWarning.warningType == 'failed-loyalty' && 'FAILURE WITH CRM'}
                            {loyaltyWarning.warningType == 'customer-not-verified' && 'CUSTOMER NOT VERIFIED'}
                            {loyaltyWarning.warningType == 'customer-inactive' && 'CUSTOMER INACTIVE'}
                            {loyaltyWarning.warningType == 'unknown' && 'UNKNOWN ERROR'}
                          </span>
                        </p>
                      )}
                      <div className="pos-bookings__actions">
                        <button onClick={() => setHasScanned(false)} className="pos-bookings__action" type="button">
                          Try Again
                        </button>
                      </div>
                    </div>
                  )}
                  {hasScanned === true && loyaltyWarning !== null && loyaltyWarning.warningType === 'photo-verification-required' && (
                    <div className="loyalty-photo-verification">
                      <p>Loyalty code requires photo verification. </p>
                      <p>Please verify the customer photo before proceeding.</p>
                      <div className="loyalty-photo-verification__image">
                        {isLoadingPhoto && <div>Loading customer photo...</div>}
                        {!isLoadingPhoto && !customerPhoto && (
                          <div>
                            <div style={{ color: 'red', padding: '5px' }}>
                              NO CUSTOMER PHOTO AVAILABLE
                              <br />
                              <br />
                              Please ask Customer to visit <br />
                              www.hey-liverpool.co.uk <br />
                              and upload their photo.
                            </div>
                            <div>
                              <p>You can still use the Loyalty, but any deals requiring Photo ID will not work.</p>
                            </div>
                          </div>
                        )}
                        {!isLoadingPhoto && customerPhoto && customerPhoto.photoBytes !== '' && (
                          <div className="loyalty-photo-verification__image-container">
                            <img
                              src={`data:image/jpeg;base64,${customerPhoto?.photoBytes}`}
                              style={{ maxHeight: '256px', marginTop: '5px' }}
                              alt="Customer Photo"
                            />
                          </div>
                        )}
                      </div>
                      <div className="pos-bookings__actions">
                        <button
                          disabled={isLoading}
                          onClick={() => {
                            doTriggerLinkLoyalty(customerCode || '', true);
                          }}
                          className="pos-bookings__action"
                          type="button"
                        >
                          {customerPhoto == undefined && <span>Continue</span>}
                          {customerPhoto != undefined && <span>Verify Photo</span>}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
              {isLoading && <div>Checking Loyalty and processing, please wait.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Loyalty;
